import React from 'react';
import { X, Moon, Sun, ChevronRight, Grid3x3 } from 'lucide-react';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import ImportExport from '../../settings/ImportExport';
import { useSwipeDismiss } from '../../../hooks/useSwipeDismiss';
import { useFocusTrap } from '../../../hooks/useFocusTrap';

interface SettingsModalProps {
  theme: string;
  toggleTheme: () => void;
  showPastDaysGreyed: boolean;
  setShowPastDaysGreyed: (value: boolean) => void;
  onOpenTagManager: () => void;
  onClose: () => void;
  events: CalendarEvent[];
  onImport: (events: any[]) => Promise<void>;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  theme, toggleTheme, showPastDaysGreyed, setShowPastDaysGreyed,
  onOpenTagManager, onClose, events, onImport
}) => {
  const { sheetRef, onTouchStart, onTouchMove, onTouchEnd } = useSwipeDismiss(onClose);
  useFocusTrap(sheetRef, true);

  return (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4 animate-fadeInBackdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div ref={sheetRef} role="dialog" aria-modal="true" className="bg-[var(--modal-bg)] w-full sm:max-w-md shadow-xl sm:rounded-xl rounded-t-2xl sm:animate-fadeIn animate-slideUp max-h-[90dvh] overflow-y-auto">
      {/* Mobile drag handle */}
      <div className="sm:hidden flex justify-center pt-2 pb-1 cursor-grab"
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      >
        <div className="w-10 h-1 rounded-full bg-[var(--content-tertiary)] opacity-40" />
      </div>
      <div className="px-6 py-4 border-b border-[var(--border-primary)] flex justify-between items-center">
        <h2 className="text-xl font-semibold text-[var(--content-primary)]">Settings</h2>
        <button onClick={onClose} className="p-1 hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors" aria-label="Close">
          <X className="w-5 h-5 text-[var(--content-secondary)]" />
        </button>
      </div>

      <div className="px-6 py-4 space-y-4">
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-[var(--content-primary)]">Appearance</h3>
          <div className="flex items-center justify-between p-3 bg-[var(--surface-tertiary)] rounded-lg">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-5 h-5 text-[var(--content-secondary)]" /> : <Sun className="w-5 h-5 text-[var(--content-secondary)]" />}
              <div>
                <div className="text-sm font-medium text-[var(--content-primary)]">Theme</div>
                <div className="text-xs text-[var(--content-secondary)]">{theme === 'dark' ? 'Dark mode' : 'Light mode'}</div>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              className={`relative w-11 h-6 rounded-full transition-colors ${theme === 'dark' ? 'bg-[var(--accent-primary)]' : 'bg-[var(--content-tertiary)]'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-[var(--content-primary)]">Display Options</h3>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showPastDaysGreyed}
              onChange={(e) => setShowPastDaysGreyed(e.target.checked)}
              className="rounded border-[var(--input-border)]"
            />
            <div>
              <div className="text-sm font-medium text-[var(--content-primary)]">Grey out past days</div>
              <div className="text-xs text-[var(--content-secondary)]">Visually distinguish past dates from future ones</div>
            </div>
          </label>
        </div>

        <div className="border-t border-[var(--border-primary)] pt-4">
          <button
            onClick={onOpenTagManager}
            className="w-full text-left px-4 py-3 bg-[var(--surface-tertiary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[var(--surface-primary)] rounded-lg">
                <Grid3x3 className="w-4 h-4 text-[var(--content-secondary)]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--content-primary)]">Manage Tags</div>
                <div className="text-xs text-[var(--content-secondary)]">Create and edit your calendars</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[var(--content-tertiary)]" />
          </button>
        </div>

        <div className="border-t border-[var(--border-primary)] pt-4">
          <ImportExport events={events} onImport={onImport} />
        </div>
      </div>
    </div>
  </div>
  );
};
